import React from "react";
import { Check, Award, Leaf, Trees, ShieldCheck, Car, Baby } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ImageBlock } from "../components/ImageBlock";
import { IMAGES } from "../data/images";
import { Seo } from "../components/Seo";

export default function About() {
  const { t } = useLang();
  const points = t("sellingPoints");

  return (
    <div data-testid="about-page" className="pt-28">
      <Seo
        title="Apie projektą — Sakurų namai Marijampolėje"
        description="Sakurų namai — išskirtinis A++ klasės kvartalas prie Pašešupio parko. Architektas G. Vieversys. Du namai: Migdolas ir Vyšnia."
        path="/projektas"
      />
      {/* header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-gold mb-4">{t("about.subtitle")}</p>
        <h1 className="font-serif text-5xl sm:text-6xl font-light text-cream">{t("about.title")}</h1>
      </section>

      {/* intro + hero image */}
      <section data-testid="about-section" className="max-w-7xl mx-auto px-6 lg:px-8 pb-20 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div className="space-y-6">
          <p className="text-cream/80 font-light leading-relaxed text-lg">{t("about.description1")}</p>
          <p className="text-cream/70 font-light leading-relaxed">{t("about.description2")}</p>
          <div className="flex flex-wrap gap-6 pt-4">
            <div className="flex items-center gap-3">
              <Award className="text-gold" size={26} strokeWidth={1.3} />
              <div>
                <p className="text-xs uppercase tracking-widest text-gold/70">{t("about.architectTitle")}</p>
                <p className="text-cream">{t("about.architectName")}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Leaf className="text-gold" size={26} strokeWidth={1.3} />
              <div>
                <p className="text-xs uppercase tracking-widest text-gold/70">{t("common.energyClass")}</p>
                <p className="text-cream">A++</p>
              </div>
            </div>
          </div>
        </div>
        <ImageBlock src={IMAGES.aerial} alt={t("about.title")} className="h-96 rounded-lg" overlay />
      </section>

      {/* buildings */}
      <section className="bg-charcoal-light py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-cream mb-12 text-center">{t("about.buildingsTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="border border-gold/20 rounded-lg overflow-hidden">
              <ImageBlock src={IMAGES.migdolasHero} alt="Migdolas" className="h-56" />
              <div className="p-8">
                <h3 className="font-serif text-3xl text-cream">Migdolas</h3>
                <p className="text-sm text-cream/60 mt-2">{t("about.migdolasDesc")}</p>
              </div>
            </div>
            <div className="border border-gold/20 rounded-lg overflow-hidden">
              <ImageBlock src={IMAGES.vysniaHero} alt="Vyšnia" className="h-56" />
              <div className="p-8">
                <h3 className="font-serif text-3xl text-cream">Vyšnia</h3>
                <p className="text-sm text-cream/60 mt-2">{t("about.vysniaDesc")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* selling points */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6">
          {Array.isArray(points) &&
            points.map((p, i) => (
              <div key={i} className="flex items-start gap-4 border-b border-cream/10 pb-5">
                <span className="mt-1 flex-shrink-0 w-6 h-6 rounded-full border border-gold/40 flex items-center justify-center">
                  <Check size={13} className="text-gold" />
                </span>
                <p className="text-cream/80 font-light leading-relaxed">{p}</p>
              </div>
            ))}
        </div>
      </section>

      {/* courtyard / amenities */}
      <section className="bg-cream text-charcoal py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl font-light mb-12 text-center">{t("about.amenitiesTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <ImageBlock src={IMAGES.courtyard} alt={t("about.amenitiesTitle")} className="h-80 rounded-lg bg-charcoal-light" />
            <div className="grid grid-cols-2 gap-6">
              {[
                { icon: Trees, label: "Pašešupio parkas" },
                { icon: Baby, label: t("about.amenitiesTitle") },
                { icon: Car, label: "Saugoma aikštelė" },
                { icon: ShieldCheck, label: "Vaizdo stebėjimas" },
              ].map((a, i) => (
                <div key={i} className="flex flex-col items-center text-center gap-3 p-6 bg-white rounded-lg border border-charcoal/10">
                  <a.icon className="text-gold-muted" size={30} strokeWidth={1.3} />
                  <span className="text-sm text-charcoal/70">{a.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
